// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createFromProduct } from "@insite/client-framework/Common/ProductInfo";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { addToChainBefore } from "@insite/client-framework/HandlerCreator";
import { executeAwaitableHandlerChain } from "@insite/client-framework/HandlerCreator";
import { replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getProductByPath } from "@insite/client-framework/Services/ProductServiceV2";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { recentlyViewedGetProductCollectionParameter } from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadProductByPath from "@insite/client-framework/Store/Data/Products/Handlers/LoadProductByPath";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import {
    chain,
    LoadProductIfNeeded,
    LoadRealTimeInventory,
    LoadRealTimePrices,
} from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

const XngAddAdditionalExpands: typeof LoadProductIfNeeded = props => {
    (props.parameter as { additionalExpands?: string[] }).additionalExpands = [
        ...((props.parameter as { additionalExpands?: string[] }).additionalExpands || []),
        "properties",
    ];
};

export const XngLoadProductIfNeeded: typeof LoadProductIfNeeded = async props => {
    // if variantTypeId is not null this has variantTraits, and we need those loaded fully for this handler chain to work correctly
    // XNG-Changes: add props.product.properties?.plumbers_lastOrderedDate condition
    if (props.product && props.product.variantTypeId === null && props.product.properties?.plumbers_lastOrderedDate) {
        if (
            !props.product.detail ||
            !props.product.content ||
            !props.product.images ||
            !props.product.documents ||
            !props.product.specifications ||
            !props.product.attributeTypes ||
            !props.product.variantTraits
        ) {
            // we don't want to wait for this if we already partially have the product, because then we can't show the partially loaded product page
            props.dispatch(
                loadProductByPath({
                    ...props.parameter,
                    addToRecentlyViewed: true,
                    onComplete: productProps => {
                        const configuration = productProps?.apiResult?.detail?.configuration;
                        if (configuration) {
                            props.dispatch({
                                type: "Pages/ProductDetails/InitConfigurationSelection",
                                configuration,
                            });
                        }

                        props.dispatch({
                            type: "Data/Products/ReplaceDataView",
                            parameter: recentlyViewedGetProductCollectionParameter,
                            dataView: {},
                        });
                    },
                }),
            );
        } else {
            getProductByPath({ ...props.parameter, addToRecentlyViewed: true, expand: ["attributes"] }).then(result => {
                props.dispatch({
                    type: "Data/Products/CompleteLoadProduct",
                    model: result,
                });
            });
        }

        return;
    }

    props.product = await executeAwaitableHandlerChain(
        loadProductByPath,
        { ...props.parameter, addToRecentlyViewed: true },
        props,
    );
};

// XNG-Changes: override to exclude variants from real time prices on first load
export const LoadRealTimePricesWithoutVariants: typeof LoadRealTimePrices = async props => {
    if (!props.product || !props.productInfosById) {
        return;
    }

    const productIds = Object.keys(props.productInfosById);

    // <XNG-Changes>: exclude variants
    // const productPriceParameters = productIds.map(productId => {
    //     const productInfo = props.productInfosById![productId];
    //     return {
    //         productId: productInfo!.productId,
    //         unitOfMeasure: productInfo!.unitOfMeasure,
    //         qtyOrdered: productInfo!.qtyOrdered,
    //     };
    // });
    const productPriceParameters = [
        {
            productId: props.product.id,
            unitOfMeasure: props.productInfosById![props.product.id]!.unitOfMeasure,
            qtyOrdered: props.productInfosById![props.product.id]!.qtyOrdered,
        },
    ];
    // </XNG-Changes>

    props.dispatch(
        loadRealTimePricing({
            productPriceParameters,
            onComplete: pricingProps => {
                if (pricingProps.apiResult?.realTimePricingResults) {
                    const pricing = pricingProps.apiResult.realTimePricingResults!.find(
                        o => o.productId === props.product?.id,
                    );
                    if (pricing) {
                        props.dispatch({
                            type: "Pages/ProductDetails/CompleteLoadRealTimePricings",
                            pricings: pricingProps.apiResult.realTimePricingResults,
                        });
                    } else {
                        props.dispatch({
                            type: "Pages/ProductDetails/FailedLoadRealTimePricings",
                            productIds,
                        });
                    }
                } else if (pricingProps.error) {
                    props.dispatch({
                        type: "Pages/ProductDetails/FailedLoadRealTimePricings",
                        productIds,
                    });
                }

                props.pricingLoaded = true;
            },
        }),
    );

    if (getSettingsCollection(props.getState()).productSettings.inventoryIncludedWithPricing) {
        await waitFor(() => !!props.pricingLoaded);
    }
};

// XNG-Changes: override to exclude variants from real time inventory on first load
export const LoadRealTimeInventoryWithoutVariants: typeof LoadRealTimeInventory = props => {
    if (
        !props.product ||
        (getIsWebCrawler() &&
            !getSettingsCollection(props.getState()).websiteSettings.includeRealTimeInventoryAndPricingForSeo)
    ) {
        return;
    }

    props.dispatch(
        loadRealTimeInventory({
            // XNG-Changes: exclude variants
            // productIds: [props.product.id, ...(props.variantChildren?.map(o => o.id) ?? [])],
            productIds: [props.product.id],
            onComplete: realTimeInventoryProps => {
                const realTimeInventory = realTimeInventoryProps?.apiResult;

                if (realTimeInventoryProps?.error) {
                    if (props.product) {
                        props.dispatch({
                            type: "Pages/ProductDetails/FailedLoadRealTimeInventory",
                            productId: props.product.id,
                        });
                    }
                } else if (realTimeInventory) {
                    if (realTimeInventory.realTimeInventoryResults && props.variantChildren && props.product) {
                        const discontinued: SafeDictionary<boolean> = {};
                        for (const result of realTimeInventory.realTimeInventoryResults) {
                            if (!result.qtyOnHand) {
                                discontinued[result.productId] = true;
                            }
                        }
                        const initialCount = props.variantChildren.length;
                        props.variantChildren = props.variantChildren.filter(
                            o => !o.isDiscontinued || (o.isDiscontinued && !discontinued[o.id]),
                        );

                        if (initialCount !== props.variantChildren.length) {
                            const variantChildrenDataView = getVariantChildrenDataView(
                                props.getState(),
                                props.product.id,
                            );
                            const { value, ...otherProps } = variantChildrenDataView;
                            props.dispatch({
                                type: "Data/Products/ReplaceDataView",
                                parameter: {
                                    productId: props.product.id,
                                    variantChildren: true,
                                },
                                dataView: {
                                    value: props.variantChildren,
                                    ...otherProps,
                                },
                            });

                            const productInfo =
                                props.getState().pages.productDetails.productInfosById?.[props.product.id] ??
                                createFromProduct(
                                    getProductState(props.getState(), props.product.id).value ?? props.product,
                                );

                            props.dispatch({
                                type: "Pages/ProductDetails/UpdateVariantSelection",
                                variantSelection: getVariantSelection(
                                    props.product,
                                    props.variantChildren,
                                    props.parameter.styledOption,
                                ),
                                variantSelectionCompleted: false,
                                selectedProductInfo: productInfo,
                            });
                        }
                    }

                    props.dispatch({
                        type: "Pages/ProductDetails/CompleteLoadRealTimeInventory",
                        realTimeInventory,
                    });
                }

                props.inventoryLoaded = true;
            },
        }),
    );
};

function getVariantSelection(product?: ProductModel, variantChildren?: ProductModel[], styledOption?: string) {
    const variantSelection: SafeDictionary<string> = {};
    if (!product?.isVariantParent || !variantChildren) {
        return variantSelection;
    }

    if (styledOption || product.defaultChildProductId) {
        const variantChild = styledOption
            ? variantChildren.find(o => o.productNumber.toLowerCase() === styledOption.toLowerCase())
            : variantChildren.find(o => o.id === product.defaultChildProductId);

        variantChild?.childTraitValues?.forEach(o => {
            variantSelection[o.styleTraitId] = o.id;
        });

        if (Object.keys(variantSelection).length > 0) {
            return variantSelection;
        }
    }

    product.variantTraits?.forEach(variantTrait => {
        const defaultVariantValue = variantTrait.traitValues?.find(traitValue => traitValue.isDefault);
        variantSelection[variantTrait.id] = defaultVariantValue?.id ?? "";
    });

    return variantSelection;
}

addToChainBefore(chain, LoadProductIfNeeded, XngAddAdditionalExpands);
replaceInChain(chain, LoadProductIfNeeded, XngLoadProductIfNeeded);
replaceInChain(chain, LoadRealTimePrices, LoadRealTimePricesWithoutVariants);
replaceInChain(chain, LoadRealTimeInventory, LoadRealTimeInventoryWithoutVariants);
